import { trpc } from './trpc';
import type {
  PromotionAnalyticsFilters,
  PromotionAnalyticsResult,
  PromotionCodeAnalyticsFilters,
  PromotionCodeAnalyticsResult,
  PromotionCodeListResult,
} from './promotionAnalytics.types';

export type {
  PromotionAnalyticsFilters,
  PromotionCodeAnalyticsFilters,
  PromotionAnalyticsResult,
  PromotionCodeAnalyticsResult,
  PromotionCodeListResult,
  PromotionCodeAnalyticsRow,
  PromotionCodeListRow,
  PromotionBadgeType,
  PromotionAnalyticsRow,
  PromotionAnalyticsSummary,
} from './promotionAnalytics.types';

type QueryOpts = Parameters<typeof trpc.orders.list.useQuery>[1];
type QueryResult<T> = Omit<ReturnType<typeof trpc.orders.list.useQuery>, 'data'> & { data: T | undefined };

/** Explicit slice — promotion analytics procedures lag in bundled AppRouter for vite-plugin-checker. */
type PromotionAnalyticsTrpcSlice = {
  analytics: {
    promotionAnalytics: {
      useQuery: (input: PromotionAnalyticsFilters, opts?: QueryOpts) => QueryResult<PromotionAnalyticsResult>;
    };
    promotionCodeAnalytics: {
      useQuery: (
        input: PromotionCodeAnalyticsFilters,
        opts?: QueryOpts,
      ) => QueryResult<PromotionCodeAnalyticsResult>;
    };
    promotionCodeList: {
      useQuery: (input: PromotionAnalyticsFilters, opts?: QueryOpts) => QueryResult<PromotionCodeListResult>;
    };
  };
};

const promotionAnalyticsTrpc = trpc as typeof trpc & PromotionAnalyticsTrpcSlice;

/** Per-promotion discount analytics for a single store. */
export function usePromotionAnalytics(filters: PromotionAnalyticsFilters, options?: { enabled?: boolean }) {
  return promotionAnalyticsTrpc.analytics.promotionAnalytics.useQuery(filters, {
    enabled: options?.enabled !== false,
    staleTime: 60_000,
  });
}

/** Per-code drill-down for one promotion. Enable only when a row is expanded. */
export function usePromotionCodeAnalytics(
  filters: PromotionCodeAnalyticsFilters,
  options?: { enabled?: boolean },
) {
  return promotionAnalyticsTrpc.analytics.promotionCodeAnalytics.useQuery(filters, {
    enabled: options?.enabled !== false,
    staleTime: 60_000,
  });
}

/** Flat per-code report for a store — every code ranked by net revenue, for affiliate payout. */
export function usePromotionCodeList(filters: PromotionAnalyticsFilters, options?: { enabled?: boolean }) {
  return promotionAnalyticsTrpc.analytics.promotionCodeList.useQuery(filters, {
    enabled: options?.enabled !== false,
    staleTime: 60_000,
  });
}
